import { CreateInvestmentInput } from "../../backend/generated/create-investment-input";
import { GetInvestmentInput } from "../../backend/generated/get-investment-input";
import { Investment } from "../../backend/generated/investment-model";
import { UpdateInvestmentInput } from "../../backend/generated/update-investment-input";
import { InvestmentStatusValues, ValidationMessages } from "../../backend/types/utils";
import { prune } from "../../utils/utility";
import { CustomValidationArguments, validateBooleansTrue, validateNumbersInRange, validateRequiredNotDefault } from "../../utils/validation";

export class InvestmentManageable extends Investment {
  constructor(obj: any) {
    super();

    obj && Object.assign(
      this,
      prune(obj),
      {
        status: obj.status ? obj.status.toLowerCase() as InvestmentStatusValues : obj.status,
        paymentMethodType: obj.paymentMethodType ? obj.paymentMethodType.toLowerCase() : obj.paymentMethodType,
      }
    );

    const statusChangeLog = [];
    if (this.statusChangeLog) {
      for (let i = 0; i < this.statusChangeLog.length; i++) {
        statusChangeLog.push(this.statusChangeLog[i]);
      }
    }
    this.statusChangeLog = statusChangeLog;
  }

  static default(): any {
    return { ...new Investment() }
  }

  isRevocable(): boolean {
    return this.status === InvestmentStatusValues.AWAITING_PAYMENT;
  }
}

export class CreateInvestmentInputExtended extends CreateInvestmentInput {
  maxAmount?: number;
  minAmount?: number;
  documents?: Array<boolean>;
}

export class CreateInvestmentManageable extends CreateInvestmentInputExtended {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        CreateInvestmentManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        CreateInvestmentManageable.default()
      );
    }
  }

  static required(): any {
    return {
      amountShares: true,
      investingForThemselves: true,
      selfDisclosedAvailableCash: false,
      selfDisclosedMonthlyNetSalary: false,
      paymentMethodId: false,
      paymentMethodType: true,
      documents: true,
    }
  }

  static default(): any {
    return {
      ...new CreateInvestmentInput(),
      investingForThemselves: false,
      minAmount: 0,
      maxAmount: undefined,
      selfDisclosedAvailableCash: false,
      selfDisclosedMonthlyNetSalary: false,
      paymentMethodType: "",
      paymentMethodId: undefined,
      documents: [],
    }
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    errors = Object.assign(
      errors,
      validateBooleansTrue(new CustomValidationArguments({
        keys: ['investingForThemselves'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    validateRequiredNotDefault(
      errors,
      CreateInvestmentManageable.required(),
      this,
      CreateInvestmentManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );

    errors = Object.assign(
      errors,
      validateNumbersInRange(
        [{
          key: 'amountShares',
          min: this.minAmount,
          max: this.maxAmount,
        }],
        errors,
        this,
        keys,
        messages[ValidationMessages.MINIMUM],
        messages[ValidationMessages.MAXIMUM],
      )
    );

    if (shouldValidate('documents')) {
      const documentsKeys = Object.keys(this.documents || []);
      let documentsErrors = validateBooleansTrue(new CustomValidationArguments({
        keys: documentsKeys,
        errors: {},
        values: this.documents?.map((document: any) => document.valid) || [],
        allowedKeys: documentsKeys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))

      documentsErrors = Object.entries(documentsErrors).reduce(
        (
          result: Array<any>, entry: any
        ) => {
          result[entry[0]] = { valid: entry[1] };

          return result;
        },
        []
      )

      if (documentsErrors.length > 0) {
        errors.documents = documentsErrors;
      }
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    data.paymentMethodType = data.paymentMethodType.toUpperCase();

    delete data.maxAmount;
    delete data.minAmount;
    delete data.documents;

    return data;
  }
}

export class UpdateInvestmentInputExtended extends UpdateInvestmentInput {
  termsRevocation?: boolean;
}

export class UpdateInvestmentManageable extends UpdateInvestmentInputExtended {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        UpdateInvestmentManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        UpdateInvestmentManageable.default()
      );
    }
  }

  required(): any {
    return { termsRevocation: this.status === InvestmentStatusValues.REVOKE_REQUESTED }
  }

  static default(): any {
    return {
      ...new UpdateInvestmentInput(),
      termsRevocation: false,
    }
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    return validateRequiredNotDefault(
      {},
      this.required(),
      this,
      UpdateInvestmentManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (data.status) {
      data.status = data.status.toUpperCase();
    }

    delete data.termsRevocation;

    return data;
  }
}

export class GetInvestmentManageable extends GetInvestmentInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        GetInvestmentManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        GetInvestmentManageable.default()
      );
    }
  }

  static required(): any {
    return { }
  }

  static default(): any {
    return { ...new GetInvestmentInput() }
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    return validateRequiredNotDefault(
      {},
      GetInvestmentManageable.required(),
      this,
      GetInvestmentManageable.default(),
      keys,
      messages[ValidationMessages.REQUIRED]
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (data.status) {
      data.status = data.status.toUpperCase();
    }

    return data;
  }
}

export type InvestmentTypeIntersection =
| CreateInvestmentInputExtended
| UpdateInvestmentInputExtended
| Investment

export type InvestmentInputTypeUnion =
& CreateInvestmentInputExtended
& UpdateInvestmentInputExtended